<script setup lang="ts">
	import enlaceLogo from '@/assets/logo-png-eb.png'
	import html2canvas from 'html2canvas'
	import jsPDF from 'jspdf'
	import { computed, ref } from 'vue'

	import DailyOccupancyChart from './charts/DailyOccupancyChart.vue'
	import DestinationsBySpendChart from './charts/DestinationsBySpendChart.vue'
	import DestinationsChart from './charts/DestinationsChart.vue'
	import MonthlyRatesChart from './charts/MonthlyRatesChart.vue'
	import OperationalMetrics from './charts/OperationalMetrics.vue'
	import SummaryKpis from './charts/SummaryKpis.vue'
	import TopHotelsChart from './charts/TopHotelsChart.vue'
	import TrendsChart from './charts/TrendsChart.vue'

	interface Props {
		companyId?: number
		companyLogo?: string
		companyName?: string
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		groupByFilter?: string
		datePreset?: string
		creationDatePreset?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = defineProps<Props>()

	const summaryKpisRef = ref<InstanceType<typeof SummaryKpis> | null>(null)
	const trendsChartRef = ref<InstanceType<typeof TrendsChart> | null>(null)
	const destinationsChartRef = ref<InstanceType<
		typeof DestinationsChart
	> | null>(null)
	const topHotelsChartRef = ref<InstanceType<typeof TopHotelsChart> | null>(
		null
	)
	const dailyOccupancyChartRef = ref<InstanceType<
		typeof DailyOccupancyChart
	> | null>(null)
	const monthlyRatesChartRef = ref<InstanceType<
		typeof MonthlyRatesChart
	> | null>(null)
	const operationalMetricsRef = ref<InstanceType<
		typeof OperationalMetrics
	> | null>(null)

	const isLoadingData = computed(() => {
		return (
			summaryKpisRef.value?.isLoading ||
			trendsChartRef.value?.isLoading ||
			destinationsChartRef.value?.isLoading ||
			topHotelsChartRef.value?.isLoading ||
			dailyOccupancyChartRef.value?.isLoading ||
			monthlyRatesChartRef.value?.isLoading ||
			operationalMetricsRef.value?.isLoading ||
			false
		)
	})

	const isExporting = ref(false)

	// Función para formatear el rango de fechas
	const formatDateRange = (range: string[] | null | undefined): string => {
		if (!range || range.length < 2) return 'No especificado'
		const formatDate = (dateStr: string) => {
			const date = new Date(dateStr)
			return date.toLocaleDateString('es-ES', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
			})
		}
		return `${formatDate(range[0])} - ${formatDate(range[1])}`
	}

	const loadImage = (src: string): Promise<HTMLImageElement> => {
		return new Promise((resolve, reject) => {
			const img = new Image()
			img.crossOrigin = 'anonymous'
			img.onload = () => resolve(img)
			img.onerror = reject
			img.src = src
		})
	}

	const exportPdf = async () => {
		// No permitir exportar si los datos están cargando
		if (isLoadingData.value) {
			console.warn('Esperando a que los datos terminen de cargar...')
			return
		}

		isExporting.value = true
		try {
			const chartsElement = document.querySelector(
				'#charts-export'
			) as HTMLElement
			if (!chartsElement) return

			const canvas = await html2canvas(chartsElement, {
				scale: 2,
				useCORS: true,
				logging: false,
				backgroundColor: '#ffffff',
			})

			const doc = new jsPDF('p', 'mm', 'a4')
			const pageWidth = 210
			const pageHeight = 297
			const margin = 10
			const logoSize = 15

			// Agregar logo de la empresa a la izquierda (si existe y no es el logo por defecto)
			if (
				props.companyLogo &&
				!props.companyLogo.includes('default_company_logo.png')
			) {
				try {
					const logoImg = await loadImage(props.companyLogo)
					const imgProps = doc.getImageProperties(logoImg)
					const aspectRatio = imgProps.width / imgProps.height

					let drawWidth = logoSize
					let drawHeight = logoSize
					if (aspectRatio > 1) {
						drawHeight = logoSize / aspectRatio
					} else {
						drawWidth = logoSize * aspectRatio
					}

					const offsetX = margin + (logoSize - drawWidth) / 2
					const offsetY = 5 + (logoSize - drawHeight) / 2

					doc.addImage(
						logoImg,
						imgProps.fileType,
						offsetX,
						offsetY,
						drawWidth,
						drawHeight
					)
				} catch (error) {
					console.warn('No se pudo cargar el logo de la empresa', error)
					doc.setFontSize(12)
					doc.text(props.companyName || '', margin, 15, {
						align: 'left',
					})
				}
			} else {
				doc.setFontSize(12)
				doc.text(props.companyName || '', margin, 15, {
					align: 'left',
				})
			}

			// Agregar logo de Enlaceforte a la derecha (manteniendo proporciones)
			const enlaceLogoHeight = logoSize
			const enlaceLogoWidth = logoSize * 2.5 // El logo es más ancho que alto
			doc.addImage(
				enlaceLogo,
				'PNG',
				pageWidth - margin - enlaceLogoWidth,
				5,
				enlaceLogoWidth,
				enlaceLogoHeight
			)

			// Encabezado del PDF (centrado entre los logos)
			doc.setFontSize(16)
			doc.setFont('helvetica', 'bold')
			doc.text('Reporte de Hoteleria', pageWidth / 2, 14, { align: 'center' })

			// Información de filtros activos
			doc.setFontSize(10)
			doc.setFont('helvetica', 'normal')

			let yPosition = 25

			// Período de búsqueda
			doc.setFont('helvetica', 'bold')
			doc.text('Periodo de busqueda:', margin, yPosition)
			doc.setFont('helvetica', 'normal')
			yPosition += 6

			// Rango de fechas de check-in
			if (props.dateRange) {
				doc.text(
					`• Fecha de check-in: ${formatDateRange(props.dateRange)}`,
					margin,
					yPosition
				)
				yPosition += 5
			}

			// Rango de fechas de creación (si existe)
			if (props.creationDateRange) {
				doc.text(
					`• Fecha de creacion: ${formatDateRange(props.creationDateRange)}`,
					margin,
					yPosition
				)
				yPosition += 5
			}

			// Fecha de generación
			const generatedDate = new Date().toLocaleDateString('es-ES', {
				day: '2-digit',
				month: 'long',
				year: 'numeric',
			})
			doc.text(`• Generado: ${generatedDate}`, margin, yPosition)
			yPosition += 8

			// Línea separadora
			doc.setDrawColor(200, 200, 200)
			doc.line(margin, yPosition, pageWidth - margin, yPosition)
			yPosition += 5

			// Calcular dimensiones de la imagen para que quepa en una sola página
			const availableWidth = pageWidth - margin * 2
			const availableHeight = pageHeight - yPosition - margin

			const imgWidth = availableWidth
			const imgHeight = (canvas.height * imgWidth) / canvas.width

			// Si la imagen es muy alta, ajustar para que quepa
			let finalWidth = imgWidth
			let finalHeight = imgHeight

			if (imgHeight > availableHeight) {
				finalHeight = availableHeight
				finalWidth = (canvas.width * finalHeight) / canvas.height
			}

			// Centrar la imagen horizontalmente
			const xPosition = (pageWidth - finalWidth) / 2

			const imgData = canvas.toDataURL('image/png', 1.0)
			doc.addImage(
				imgData,
				'PNG',
				xPosition,
				yPosition,
				finalWidth,
				finalHeight
			)

			const filename = `reporte_hoteles_${new Date().toISOString().split('T')[0]}.pdf`
			doc.save(filename)
		} catch (error) {
			console.error('Error al exportar PDF:', error)
		} finally {
			isExporting.value = false
		}
	}

	// Exponer funciones y estado para uso externo
	defineExpose({
		exportPdf,
		isExporting,
		isLoadingData,
	})
</script>

<template>
	<div class="relative">
		<!-- Botón de exportación flotante eliminado - ahora se controla desde el padre -->
		<div
			class="px-4 pb-4"
			id="charts-export"
		>
			<div class="space-y-4">
				<!-- KPIs del resumen -->
				<div>
					<h2 class="text-2xl font-bold text-gray-900 mb-3">Resumen General</h2>
					<SummaryKpis
						ref="summaryKpisRef"
						:company-id="companyId"
						:date-range="dateRange"
						:creation-date-range="creationDateRange"
						:status-filter="statusFilter"
						:search-traveler-id="searchTravelerId"
						:search-hotel-id="searchHotelId"
						:created-at="createdAt"
					/>
				</div>
				<!-- Gráficos -->
				<div>
					<h2 class="text-2xl font-bold text-gray-900 mb-3">
						Análisis de Datos
					</h2>
					<div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
						<!-- Gráfico de Tendencias -->
						<TrendsChart
							ref="trendsChartRef"
							:company-id="companyId"
							:date-range="dateRange"
							:creation-date-range="creationDateRange"
							:status-filter="statusFilter"
							:group-by-filter="groupByFilter"
							:search-traveler-id="searchTravelerId"
							:search-hotel-id="searchHotelId"
							:created-at="createdAt"
							:order-by="orderBy"
							:order-direction="orderDirection"
						/>

						<!-- Tarifa Promedio Diaria por Mes -->
						<MonthlyRatesChart
							ref="monthlyRatesChartRef"
							:company-id="companyId"
							:date-range="dateRange"
							:creation-date-range="creationDateRange"
							:status-filter="statusFilter"
							:group-by-filter="groupByFilter"
							:search-traveler-id="searchTravelerId"
							:search-hotel-id="searchHotelId"
							:created-at="createdAt"
							:order-by="orderBy"
							:order-direction="orderDirection"
						/>

						<!-- Gráfico de Hoteles Más Utilizados -->
						<TopHotelsChart
							ref="topHotelsChartRef"
							:company-id="companyId"
							:date-range="dateRange"
							:creation-date-range="creationDateRange"
							:status-filter="statusFilter"
							:group-by-filter="groupByFilter"
							:search-traveler-id="searchTravelerId"
							:search-hotel-id="searchHotelId"
							:created-at="createdAt"
							:order-by="orderBy"
							:order-direction="orderDirection"
						/>

						<!-- Gráfico de Ocupación por Día -->
						<DailyOccupancyChart
							ref="dailyOccupancyChartRef"
							:company-id="companyId"
							:date-range="dateRange"
							:creation-date-range="creationDateRange"
							:status-filter="statusFilter"
							:group-by-filter="groupByFilter"
							:search-traveler-id="searchTravelerId"
							:search-hotel-id="searchHotelId"
							:created-at="createdAt"
							:order-by="orderBy"
							:order-direction="orderDirection"
						/>

						<!-- Gráfico de Destinos -->
						<DestinationsChart
							ref="destinationsChartRef"
							:company-id="companyId"
							:date-range="dateRange"
							:creation-date-range="creationDateRange"
							:status-filter="statusFilter"
							:group-by-filter="groupByFilter"
							:search-traveler-id="searchTravelerId"
							:search-hotel-id="searchHotelId"
							:created-at="createdAt"
							:order-by="orderBy"
							:order-direction="orderDirection"
						/>

						<!-- Mixed Chart -->
						<DestinationsBySpendChart
							ref="destinationsBySpendChart"
							:company-id="companyId"
							:date-range="dateRange"
							:creation-date-range="creationDateRange"
							:status-filter="statusFilter"
							:group-by-filter="groupByFilter"
							:search-traveler-id="searchTravelerId"
							:search-hotel-id="searchHotelId"
							:created-at="createdAt"
							:order-by="orderBy"
							:order-direction="orderDirection"
						/>
					</div>
				</div>

				<!-- Métricas Adicionales -->
				<div>
					<h2 class="text-2xl font-bold text-gray-900 mb-3">
						Métricas Operacionales
					</h2>
					<OperationalMetrics
						ref="operationalMetricsRef"
						:company-id="companyId"
						:date-range="dateRange"
						:creation-date-range="creationDateRange"
						:status-filter="statusFilter"
						:group-by-filter="groupByFilter"
						:search-traveler-id="searchTravelerId"
						:search-hotel-id="searchHotelId"
						:created-at="createdAt"
						:order-by="orderBy"
						:order-direction="orderDirection"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
